import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";

const faqs = [
  {
    q: "小虎看番是做什么的？",
    a: "小虎看番是一个专注补番推荐的动画片单站，通过高分番列表、冷门佳作、完结番合集与熬夜补番清单，帮大家快速找到下一部值得看的好番。",
  },
  {
    q: "小虎为什么会叫「看番」？",
    a: "「看番」取自「看动画番剧」的说法，小虎是一只想帮大家解决「今天看什么」这只老虎，于是就用「小虎看番」这个名字，陪大家一起追番。",
  },
  {
    q: "片单是完全免费的吗？",
    a: "是的，小虎看番的所有片单内容完全免费，只为帮大家发现好番，不涉及任何商业推广性质的搬运与导流。",
  },
  {
    q: "我可以投稿推荐一部番吗？",
    a: "非常欢迎！通过页脚的联系邮箱把你想推荐的番发给我们，经小虎和观众确认口碑后，就有机会被收录进对应片单。",
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
        aria-expanded={open}
      >
        <h3 className="text-sm font-semibold text-foreground-900 md:text-base">{q}</h3>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          <i className="ri-arrow-down-s-line text-lg" aria-hidden="true"></i>
        </span>
      </button>
      {open && (
        <div className="border-t border-background-100 px-4 py-4">
          <p className="text-sm leading-relaxed text-foreground-700">{a}</p>
        </div>
      )}
    </div>
  );
}

const testimonials = [
  {
    name: "阿橘",
    role: "新番路人",
    text: "之前一到周末就不知道看啥，现在直接来小虎翻熬夜清单，已经连着好几周没剧荒了。",
  },
  {
    name: "未闻_花名",
    role: "治愈党",
    text: "冷门佳作这个分类真的挖到宝，冰菓和虫师都是在小虎这儿发现的，后悔没早点看！",
  },
  {
    name: "补番狂魔",
    role: "二刷达人",
    text: "高分榜不含水分，钢炼和命运石之门都是当年在小虎种草后封神的，值得信赖。",
  },
];

const values = [
  {
    icon: "ri-focus-3-line",
    title: "只推好番",
    desc: "宁缺毋滥，每一部片单都经过口碑把关，不为了凑数塞进不配位的作品。",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "分类清晰",
    desc: "高分、冷门、完结、熬夜四大分类各司其职，让你按需取用，精准告别剧荒。",
  },
  {
    icon: "ri-empathize-line",
    title: "常更常新",
    desc: "每周五更新片单，跟着新番动态调整熬夜清单，永远不让你错过好内容。",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于小虎看番",
      description: "小虎看番的品牌介绍、发展理念、内容原则与用户口碑。",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="关于我们 | 小虎看番 · 告别剧荒的补番推荐站"
        description="了解小虎看番的品牌故事、发展理念与内容原则。小虎看番专注高分番列表、冷门佳作与熬夜补番清单，陪你告别剧荒。"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-24 md:px-6">
        <nav aria-label="面包屑" className="mb-6">
          <ol className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600">首页</Link>
            </li>
            <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
            <li className="text-foreground-800" aria-current="page">关于我们</li>
          </ol>
        </nav>

        {/* 品牌头图 */}
        <section className="relative overflow-hidden rounded-lg">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20illustration%20of%20a%20small%20orange%20tiger%20curled%20up%20reading%20a%20giant%20storybook%20of%20anime%20posters%2C%20cozy%20bookshelf%20background%20with%20warm%20lamp%20light%2C%20golden%20amber%20and%20cream%20palette%2C%20charming%20storybook%20style%20illustration&width=1400&height=500&seq=xh-about-01&orientation=landscape"
            alt="小虎看番品牌故事插画"
            className="h-52 w-full object-cover object-center md:h-72"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 to-primary-900/30"></div>
          <div className="absolute inset-0 flex items-center px-5 md:px-8">
            <div className="max-w-lg">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-foreground-900">
                <i className="ri-fire-fill" aria-hidden="true"></i>
                关于小虎
              </span>
              <h1 className="mt-3 font-display text-3xl text-background-50 md:text-4xl">
                一只老虎，帮你告别剧荒
              </h1>
            </div>
          </div>
        </section>

        {/* 品牌介绍 */}
        <section className="mt-12">
          <div className="grid gap-8 md:grid-cols-2 md:gap-12">
            <div>
              <h2 className="font-display text-2xl text-foreground-950">小虎看番是谁</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                小虎看番是一个专注「补番推荐」的动画片单站。我们相信，好番很多，
                缺的从来不是作品，而是一份靠谱的片单。于是小虎把高分番列表、冷门佳作、
                完结番合集与熬夜补番清单整理成档，让每个人都能按心情、按时间、按口味找到下一部想看的番。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                无论你是想刷一遍经典、还是想挖一部冷门宝藏，小虎都为你准备好了一份合口味的片单。
                我们坚持免费、无套路、只看口碑，把「今天看什么」这个每天的难题，变成随手点开的安心。
              </p>
            </div>
            <div>
              <h2 className="font-display text-2xl text-foreground-950">我们的内容原则</h2>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true"></i>
                  <span>口碑导向：所有作品都经观众评分与讨论验证，拒绝为流量硬推。</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true"></i>
                  <span>真实整理：片单信息（年份、话数、类型）经过人工核对，不凭空捏造。</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true"></i>
                  <span>持续更新：每周五更新片单，跟随新番动态调整，常看常新。</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true"></i>
                  <span>尊重原创：片单仅作内容推荐，不提供盗版资源与搬运导流。</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* 理念卡片 */}
        <section className="mt-14">
          <div className="mb-6 text-center">
            <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">我们坚持的三件事</h2>
            <p className="mt-2 text-sm text-foreground-600">把每一份片单做对、做准、做贴心</p>
          </div>
          <div className="grid gap-4 md:grid-cols-3 md:gap-5">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className={`${v.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-display text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 用户口碑 */}
        <section className="mt-14 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
            <div className="mb-6 text-center">
              <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">他们说</h2>
              <p className="mt-2 text-sm text-foreground-600">来自正在补番的观众朋友</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3 md:gap-5">
              {testimonials.map((t) => (
                <figure
                  key={t.name}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <div className="flex gap-0.5 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill" aria-hidden="true"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-800">
                    “{t.text}”
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-semibold text-secondary-900">
                      {t.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-14" id="faq">
          <div className="mb-6">
            <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">常见问题</h2>
            <p className="mt-2 text-sm text-foreground-600">关于小虎看番，你可能还想知道的</p>
          </div>
          <div className="max-w-3xl space-y-3">
            {faqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </section>

        {/* 联系方式 */}
        <section className="mt-14 rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div>
              <h2 className="font-display text-xl text-foreground-950 md:text-2xl">想找小虎聊聊？</h2>
              <p className="mt-1 text-sm text-foreground-700">
                投稿推荐、意见反馈、合作咨询，欢迎随时通过邮箱联系我们。
              </p>
            </div>
            <a
              href="mailto:hi@xiaohu.fan"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              hi@xiaohu.fan
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}